// Zustand des App-Rahmens (zustand): Sidebar, Gruppen, Befehlspalette, Scan-Dialog, Benachrichtigungen,
// KI-Assistent. Was eine Vorliebe ist (eingeklappt, Assistent offen), merkt sich das Gerät.
import { create } from 'zustand'
import { readStored, writeStored } from '../lib/storage'

export type NotificationTab = 'new' | 'all' | 'tasks'

export interface ShellState {
  /** vom Nutzer eingeklappt (⌘B); die Hülle klappt zusätzlich von selbst ein, wenn der Platz fehlt */
  sidebarCollapsed: boolean
  /** eingeklappte Gruppen der Navigation (Schlüssel aus NAV_SECTIONS) */
  collapsedSections: string[]
  /** Navigation als Drawer auf dem Telefon („Mehr“) */
  mobileNavOpen: boolean
  paletteOpen: boolean
  /** Text, mit dem die Palette aufgeht (':' für den Standortwechsel) */
  paletteQuery: string
  scanOpen: boolean
  notificationsOpen: boolean
  notificationsTab: NotificationTab
  assistantOpen: boolean
  /** Frage, die aus der Palette an den Assistenten übergeben wurde */
  assistantSeed: string | null
}

const SIDEBAR_KEY = 'shell.sidebarCollapsed'
const SECTIONS_KEY = 'shell.collapsedSections'
const ASSISTANT_KEY = 'shell.assistantOpen'

export const useShellStore = create<ShellState>(() => ({
  sidebarCollapsed: readStored<boolean>(SIDEBAR_KEY, false) === true,
  collapsedSections: ((): string[] => {
    const stored = readStored<unknown>(SECTIONS_KEY, [])
    return Array.isArray(stored) ? stored.filter((entry): entry is string => typeof entry === 'string') : []
  })(),
  mobileNavOpen: false,
  paletteOpen: false,
  paletteQuery: '',
  scanOpen: false,
  notificationsOpen: false,
  notificationsTab: 'new',
  assistantOpen: readStored<boolean>(ASSISTANT_KEY, false) === true,
  assistantSeed: null,
}))

const set = useShellStore.setState

/** Person, deren Vorlieben für die Gruppen der Navigation gelten – null: die des Geräts (vor der Anmeldung) */
let sectionsOwner: string | null = null
const sectionsKey = (owner: string | null): string => (owner ? `${SECTIONS_KEY}.${owner}` : SECTIONS_KEY)
const storedSections = (key: string): string[] | null => {
  const stored = readStored<unknown>(key, null)
  return Array.isArray(stored) ? stored.filter((entry): entry is string => typeof entry === 'string') : null
}

export const shell = {
  toggleSidebar(): void {
    const next = !useShellStore.getState().sidebarCollapsed
    writeStored(SIDEBAR_KEY, next)
    set({ sidebarCollapsed: next })
  },
  /**
   * Eingeklappte Gruppen merkt sich das Gerät JE PERSON: Nach der Anmeldung gelten die Vorlieben dieser Person
   * (beim ersten Mal die bisherigen des Geräts), nach dem Abmelden wieder die des Geräts.
   */
  setPerson(userId: string | null): void {
    if (userId === sectionsOwner) return
    sectionsOwner = userId
    set({ collapsedSections: storedSections(sectionsKey(userId)) ?? storedSections(SECTIONS_KEY) ?? [] })
  },
  toggleSection(key: string): void {
    const current = useShellStore.getState().collapsedSections
    const next = current.includes(key) ? current.filter((entry) => entry !== key) : [...current, key]
    writeStored(sectionsKey(sectionsOwner), next)
    set({ collapsedSections: next })
  },
  setMobileNav(open: boolean): void {
    set({ mobileNavOpen: open })
  },
  openPalette(query = ''): void {
    set({ paletteOpen: true, paletteQuery: query, mobileNavOpen: false })
  },
  setPalette(open: boolean): void {
    set(open ? { paletteOpen: true } : { paletteOpen: false, paletteQuery: '' })
  },
  setScan(open: boolean): void {
    set({ scanOpen: open, ...(open ? { mobileNavOpen: false } : {}) })
  },
  openNotifications(tab: NotificationTab = 'new'): void {
    set({ notificationsOpen: true, notificationsTab: tab, mobileNavOpen: false })
  },
  setNotifications(open: boolean): void {
    set({ notificationsOpen: open })
  },
  setNotificationsTab(tab: NotificationTab): void {
    set({ notificationsTab: tab })
  },
  setAssistant(open: boolean, seed: string | null = null): void {
    writeStored(ASSISTANT_KEY, open)
    set({ assistantOpen: open, assistantSeed: open ? seed : null, ...(open ? { mobileNavOpen: false } : {}) })
  },
  toggleAssistant(): void {
    shell.setAssistant(!useShellStore.getState().assistantOpen)
  },
  clearAssistantSeed(): void {
    set({ assistantSeed: null })
  },
  /** Zustand ohne Vorlieben zurücksetzen (Abmelden, Tests) */
  closeAll(): void {
    set({ mobileNavOpen: false, paletteOpen: false, paletteQuery: '', scanOpen: false, notificationsOpen: false })
  },
}
